import { z } from "zod"
import { ServiceBikeType } from "../service/service-bike"
import { UniversalJobCardBike } from "../service/universal-job-card"

export type MilestoneCategory =
  | "RECEIVED"
  | "INSPECTED"
  | "SERVICED"
  | "BRAND"
  | "BIKE_TYPE"

/** Lowest to highest: van, mästare, expert, specialist, legend. */
export type MilestoneTier =
  | "EXPERIENCED"
  | "MASTER"
  | "EXPERT"
  | "SPECIALIST"
  | "LEGEND"

/** Assessed components that make a complete inspection. */
export const COMPLETE_INSPECTION_COMPONENTS = 30

const MAX_QUALITY_PERCENT = 100
const QUALITY_PERCENT_PER_COMPONENT =
  MAX_QUALITY_PERCENT / COMPLETE_INSPECTION_COMPONENTS

/** An even share per assessed component, up to a complete inspection. */
export const toInspectionQualityPercent = (components: number): number =>
  Math.min(components * QUALITY_PERCENT_PER_COMPONENT, MAX_QUALITY_PERCENT)

/** A whole year, or one of its months. */
export interface AchievementPeriod {
  year: number
  /** 0 is January. Null means the whole year. */
  month: number | null
}

/** Adds up to the milestone's count. A bike that got both counts in both. */
export interface MilestoneWorkBreakdown {
  inspected: number
  serviced: number
}

export interface AchievementMilestone {
  /** Identifies the milestone across requests, whatever its count. */
  key: string
  category: MilestoneCategory
  tier: MilestoneTier
  /** The bikes it takes to earn the milestone. */
  requiredCount: number
  experiencePoints: number
  /** The moment the mechanic reached the milestone's count. */
  reachedAt: string
  /** The bikes behind the milestone. Set on BRAND and BIKE_TYPE milestones. */
  workBreakdown: MilestoneWorkBreakdown | null
  /** Set on BRAND milestones. */
  brandName: string | null
  /** Set on BIKE_TYPE milestones. */
  bikeType: ServiceBikeType | null
}

/** How close a mechanic is to the next milestone on a track. */
export interface MilestoneProgress {
  /** Matches the key of the milestone once it's reached. */
  key: string
  category: MilestoneCategory
  tier: MilestoneTier
  currentCount: number
  requiredCount: number
  /** Rounded down, so a bar is never full before the milestone is reached. */
  progressPercent: number
  /** The bikes so far. Set on BRAND and BIKE_TYPE milestones. */
  workBreakdown: MilestoneWorkBreakdown | null
  /** Set on BRAND milestones. */
  brandName: string | null
  /** Set on BIKE_TYPE milestones. */
  bikeType: ServiceBikeType | null
}

export interface InspectionScore {
  alfredPoints: number
  inspectedSharePercent: number
  qualityPercent: number
  inspectedJobs: number
  servicedJobs: number
}

export interface CustomerRatingScore {
  alfredPoints: number
  /** Null until the first review of the period. */
  rating: number | null
  reviewCount: number
}

/** One customer review behind a rating card. */
export interface AchievementReview {
  jobId: string
  displayNumber: number
  bike: UniversalJobCardBike | null
  rating: number
  /** Null when the customer left no comment. */
  comment: string | null
  reviewedAt: string
}

export interface AchievementSummary {
  alfredPoints: number
  inspection: InspectionScore
  /** Null when the store has turned off Julo reviews. */
  customerRating: CustomerRatingScore | null
}

export interface MechanicAchievementSummary extends AchievementSummary {
  mechanicId: string
  mechanicName: string
  /** Never resets, unlike Alfred points which cover the selected period. */
  experiencePoints: number
  milestones: AchievementMilestone[]
  /**
   * The next tier on every track with a reached milestone, plus the tracks
   * closest to their first once past halfway. Closest to its tier first.
   */
  nextMilestones: MilestoneProgress[]
}

export interface PlacedMechanicAchievementSummary
  extends MechanicAchievementSummary {
  /**
   * Placement among the store's mechanics by Alfred points in the period, 1 is
   * first. Null without points.
   */
  place: number | null
}

export interface AchievementsResponse {
  period: AchievementPeriod
  /** Every year and month with work to show, each year before its months, newest first. */
  periods: AchievementPeriod[]
  store: AchievementSummary
  mechanics: PlacedMechanicAchievementSummary[]
}

/** Milestones the mechanic hasn't been congratulated on yet, oldest first. */
export interface UnseenMilestones {
  milestones: AchievementMilestone[]
  /** What the milestones are worth together. */
  experiencePoints: number
}

export const MarkMilestonesSeenSchema = z.object({
  keys: z.array(z.string().min(1).max(512)),
})

export type MarkMilestonesSeen = z.infer<typeof MarkMilestonesSeenSchema>

/** One value per month, January first. */
export type MonthlyValues = number[]

export interface MonthlyMetric {
  /** Covers the selected period. Null when it has nothing to measure. */
  total: number | null
  /** Always the whole year, so a selected month is seen in context. */
  monthly: MonthlyValues
}

export interface BrandServiceCount {
  brandName: string
  serviced: MonthlyMetric
}

export interface MechanicAchievementsResponse
  extends MechanicAchievementSummary {
  period: AchievementPeriod
  periods: AchievementPeriod[]
  monthlyInspectedSharePercent: MonthlyValues
  monthlyQualityPercent: MonthlyValues
  /** Null when the store has turned off Julo reviews. */
  monthlyRating: MonthlyValues | null
  received: MonthlyMetric
  inspected: MonthlyMetric
  serviced: MonthlyMetric
  topBrands: BrandServiceCount[]
  receptionSeconds: MonthlyMetric
  inspectionSeconds: MonthlyMetric
  averageOrder: MonthlyMetric
  revenue: MonthlyMetric
}
